import * as amplify from "./amplify.ts";
import * as i18n from "../i18n.ts";
import * as libLocation from "../lib/location.ts";
import * as modal from "./modal.ts";
import ApiServer from "../ApiServer.ts";
import * as Html from "./Html.ts";
import * as FwDatas from "../FwDatas.ts";
import TPL_TASK_CONTAINER_CONTENT from "../list/task-container-content.mustache";
import {toUnicodeVariant} from "../lib/toUnicodeVariant.ts"
import Icons from "../svg/Icons.ts";
import { registerDragStart} from "../lib/FileUtils.ts";
import { MetaTaskCache, TaskCategoryCache } from "../server/objects/TaskBeans.ts";
import * as segment from "../tracking-segment.ts";
import TPL_tasklist_list_item from "../list/tasklist_list_item.mustache"
import TPL_task_item from "../list/task.mustache"
import { checkTaskLimitOntry, taskdeletemarkedraw, taskHardLimitReach } from "../list/list_clean.ts";
import { itemName, listsRendered, revealItems, toastItemAdded } from "../list/task_undo.ts";
import { parseLeadingQuantity, PIECE } from "../lib/ingredientParser.ts";
import { clampQuantity } from "../lib/unitFormat.ts";
import { quantityPicker } from "../widget/action-menu-content.ts";
import { isFeatureEnabled } from "../lib/launchdarkly/launchdarkly.ts";
import { type ITaskList, type TaskBean, type TaskListTypeEnum } from "../server/fizapilib.auto.ts";


/**
 * The one creation modal, wherever a list is created from. Resolves with the created list, or with
 * nothing when the modal is closed without saving: the caller decides where the user lands then.
 * @param options.lockType hides the list type, for a caller that only accepts the type it asked for
 */
export function openListCreate(type :TaskListTypeEnum, options :{lockType ?:boolean} = {}) :Promise<ITaskList|undefined>{
  return import("../list/list_create.mustache").then(TPL=>new Promise<ITaskList|undefined>(function(resolve){
    const
      list = new ApiServer.types.ITaskList({taskListType:type,sharedToAll:"true",rights:{},sharedMemberIds:[]}),
      theModal = modal.openDomModal(TPL,Object.assign(list,{typeLocked:options.lockType})),
      form = theModal.querySelector("form") as HTMLFormElement;

    //closing without saving resolves with nothing, the first resolve is the one that counts
    theModal.onClose = ()=>resolve(undefined);
    form.setAttribute('data-fw-dyn-action','custom.listcreate');
    ApiServer["custom.listcreate"] = ApiServer.taskcreatelist.prepare(function(created){
      segment.track("List Created");
      resolve(created);
      modal.closeDomModal(theModal);
      amplify.publish("taskNeedRefresh");
    });
  }));
}

async function listcreatereally(type){
  modal.closeDomModal();
  const created = await openListCreate(type);
  //the lists page opens the fresh list
  if(created){
    libLocation.goTo('?metaId='+created.metaId,true)
  }
}

Html.registerDom({
  async taskmarkinlist(complete){
    const
      node = Html.getDomMetaIdNode(this),
      metaId = Html.getDomMetaId(this),
      [updated,[lists]] = await Promise.all(
        [
          ApiServer.taskmark.setOptions({progressbar:'none'})(metaId,complete),
          MetaTaskCache.refresh()
        ]),
      currentList = lists.find(list=>list.metaId === updated.taskListId);
    if(String(complete)==='true'){
      segment.track("Task Completed",{list_type:segment.enumValue(currentList?.taskListType)});
    }

    let listItemNode = document.querySelector(`.selectable>*[data-fw-meta-id="${currentList.metaId}"]`) as HTMLElement;
    const isSelected = listItemNode?.classList.contains('selected');

    listItemNode = Html.replaceWithSingleDom(listItemNode,TPL_tasklist_list_item,currentList);
    if(listItemNode){
      listItemNode.classList.toggle('selected',isSelected);
    }

    Html.replaceWithSingleDom(node,TPL_task_item,updated);
  },
  async listcreate(){
    const button = await modal.pick({
      title:"catsystem.picktype",
      cssClass:"large",
      gap:"var(--space-s)",
      button:[
        {title:"catsystem.SYS-CAT-SHOPPINGLIST",text:"catsystem.SYS-CAT-SHOPPINGLISTtext",icon:Icons.list.create_list_type_illustration_groceries,onclick:()=>listcreatereally("SHOPPING_LIST")},
        {title:"catsystem.SYS-CAT-TODOS",text:"catsystem.SYS-CAT-TODOStext",icon:Icons.list.create_list_type_illustration_todo,onclick:()=>listcreatereally("TODOS")},
        {title:"catsystem.SYS-CAT-OTHER",text:"catsystem.SYS-CAT-OTHERtext",icon:Icons.list.create_list_type_illustration_other,onclick:()=>listcreatereally("OTHER")}

      ]})
    if(button){
      button.onclick()
    }

  },
  listcreatereally,

  async listdelete(){
    const
      metaId = Html.getDomMetaId(this),
      list = FwDatas.get(metaId);
    modal.closeDomModal();
    if(list.system){
      if(!await modal.confirmDelete({
        title:"lists_delete_system_list_confirmation_title",
        message:"lists_delete_system_list_confirmation_message"
      })) return;
    }else{
      if(!await modal.confirmDelete("catsystem.deleteconfirm")) return;
    }
    ApiServer.taskdeletelist(metaId);
    amplify.publish("taskNeedRefresh");
  },
  listedit(){
    const
      catNode = Html.getDomMetaIdNode(this),
      list = FwDatas.get([Html.getDomMetaId(catNode)]);
    import("../list/list_create.mustache").then(function(TPL){
      const
        theModal = modal.openDomModal(TPL,list),
        form = theModal.querySelector("form") as HTMLFormElement;

      form.setAttribute('data-fw-dyn-action','custom.listedit');
      ApiServer['custom.listedit'] = ApiServer.taskupdatelist.prepare({metaId:list.metaId},function(){
        amplify.publish("taskNeedRefresh")
        modal.closeDomModal(theModal);
      });
    });
  },
  listeditsharing(){
    const
      catNode = Html.getDomMetaIdNode(this),
      list = FwDatas.get([Html.getDomMetaId(catNode)]);
    import("../list/list_editsharing.mustache").then(function(TPL){
      const theModal = modal.openDomModal(TPL,list);
      const form = theModal.querySelector("form") as HTMLFormElement;
      form.addEventListener("submit", function(this:HTMLFormElement,evt){
        evt.preventDefault();
        modal.closeDomModal(theModal);
        ApiServer.taskupdatelist(this)
        amplify.publish("taskNeedRefresh")
      });
    });
  },
  listshowhidecompleted(completedHidden){
    const metaId = Html.getDomMetaId(this);

    //do not send all list beause of a serveur bug
    ApiServer.taskupdatelist({metaId,completedHidden});
    amplify.publish('taskNeedRefresh')

  },
  taskmarkall(){
    const metaId = Html.getDomMetaId(this);

    ApiServer.taskmetamark([metaId],true);
    amplify.publish("taskNeedRefresh")
   },
  taskunmarkall(){
    const metaId = Html.getDomMetaId(this);

    ApiServer.taskmetamark([metaId],false);

    amplify.publish("taskNeedRefresh")
  },
  taskdeletemarked(metaId = Html.getDomMetaId(this)){
    segment.track("Task List Cleanup Tapped");
    taskdeletemarkedraw(metaId)
  },
  taskduplicatelist(){
    const metaId = Html.getDomMetaId(this);

    ApiServer.taskduplicatelist(metaId,function(newList){
      modal.snackDone('catsystem.duplicatedone')
      libLocation.goTo('?metaId='+newList.metaId,true)
    },e=>{
      if(e.code==='610' && e.type==='task'){
        taskHardLimitReach(metaId)
      }else{
        throw e;
      }
    });
    amplify.publish("taskNeedRefresh")
  },
  tasklistsort(sortby){
    const
      catNode = Html.getDomMetaIdNode(this),
      list = FwDatas.get([Html.getDomMetaId(catNode)]);

    list.taskSorting = sortby;
    //do not send all list beause of a serveur bug
    ApiServer.taskupdatelist.setOptions({progressbar:'none'})({metaId:list.metaId,taskSorting:sortby});
    list.sort();
    import("../list/tasksortby.mustache").then(function(TPLSORT){
      const taskContainer = catNode.querySelector<HTMLElement>('#task-container');
      const sortingTasks = catNode.querySelector<HTMLElement>('.fw-sorting-tasks');
      if(taskContainer) Html.setHtml(taskContainer,TPL_TASK_CONTAINER_CONTENT,list);
      if(sortingTasks) Html.setHtml(sortingTasks,TPLSORT,list);
    });
  },

  tasklistcategoryhide(hide){
    const
      catNode = Html.getDomMetaIdNode(this),
      list = FwDatas.get([Html.getDomMetaId(catNode)]);
    list.taskCategoriesHidden = hide;
    ApiServer.taskupdatelist.setOptions({progressbar:'none'})({metaId:list.metaId,taskCategoriesHidden:hide});
    amplify.publish("taskNeedRefresh");
  },
  async listshare(){
    const
      catNode = Html.getDomMetaIdNode(this),
      list = FwDatas.get([Html.getDomMetaId(catNode)]),
      taskByCategory = list.taskByCategory();

    let text;

    if(list.taskCategoriesHidden){
      text = `${list.name}
${list.ongoing.map(task=>`  □ ${task.text}`).join('\r\n')}
${list.complete.map(task=>`  ✓ ${task.text}`).join('\r\n')}
`;
    }else{
      text = `${toUnicodeVariant(list.name.toLocaleUpperCase(),'bold')}
${taskByCategory.map(pair=>`
${pair.category.emoji}\t${toUnicodeVariant(pair.category.name.toLocaleUpperCase(),'bold')}
${pair.items.map(task=>`\u00a0\u00a0\u00a0\u00a0\u00a0\u00a0${task.computedComplete?'🗹':'❏'}\u00a0\u00a0${toUnicodeVariant(task.text,'italic',task.complete?'strike':undefined)}`).join('\n')}
`).join('')}

${i18n.get('share.footer')}
\u00a0
`;
    }
    try{
      await navigator.share({text})
    }catch(e){
      modal.oops('fw2.defaultError')
    }
  }


});

/**
 * Whether the added quantity can be summed into the entry: it can when the entry counts in the same
 * unit, and when the entry has no quantity yet — the added one then simply becomes its own.
 */
function sameUnit(task :TaskBean, unit :string){
  //a bare count is written with no unit by some clients, PIECE by ours
  return !(task.quantity>0) || (unit||PIECE)===(task.unit||PIECE);
}

/** An entry carrying more than its name and quantity: merging into it would hide what it holds. */
function hasSpecificity(task :TaskBean){
  return !!(task.medias?.length || task.dueDate || task.refId);
}

/**
 * The ongoing entry the added line should be summed into: same name, summable unit, and plain on
 * both sides. A different unit means the user wants a second line, per the MVP of FWWEB-1812.
 */
function findDuplicate(list :ITaskList, text :string, unit :string, added :{dueDate :boolean, attachment :boolean}){
  if(added.dueDate || added.attachment){
    return undefined;
  }
  return list.ongoing.find(task=>task.text.toLocaleLowerCase() ===text.toLocaleLowerCase() && task.editable && sameUnit(task,unit) && !hasSpecificity(task));
}
ApiServer["custom.taskquickadd"] = async function(form: HTMLFormElement){
  const
    catNode = Html.getDomMetaIdNode(form),
    taskListId = Html.getDomMetaId(catNode),
    list = FwDatas.get(taskListId),
    formData = ApiServer.flattenForm(form),
    //read before the reset below wipes them: they are specificities of the line being added
    added = {
      dueDate:!['','$empty',undefined].includes((form as any).dueDate?.value),
      attachment:!!(form as any).picture?.customFiles?.length
    };
  let
    text = formData.text[0],
    quantity :number,
    unit :string;

  //everything quantity below hangs on the flag: with it off, the quick add behaves as it always did
  const quantityAware = list.taskListType==='SHOPPING_LIST' && isFeatureEnabled("FWALL-33-list-item-quantity");

  //an url is fetched as an og bean below, its digits are not a quantity
  if(quantityAware && text.indexOf('http')!==0){
    const parsed = parseLeadingQuantity(text,i18n.getShortLanguage());
    //a quantity with nothing after it is not an item, let the user finish typing. A number we cannot
    //store is not a quantity either: "1500g flour" stays a name rather than becoming 999.9g of it.
    if(parsed && parsed.name && clampQuantity(parsed.quantity)===parsed.quantity){
      text = parsed.name;
      quantity = parsed.quantity;
      unit = parsed.unit;
      formData.text = [parsed.name];
      formData.quantity = [parsed.quantity];
      formData.unit = [parsed.unit];
    }
  }

  //a due date is meant to be reminded of, as on the apps: the calendar default reminder comes with it
  const defaultReminder = added.dueDate && FwDatas.getSettingsPerFamily()?.defaultReminderAsString();
  if(defaultReminder){
    const [reminderUnit,reminderValue] = defaultReminder.split('-');
    formData['reminder.reminderType'] = ['SNOOZE'];
    formData['reminder.reminderUnit'] = [reminderUnit];
    formData['reminder.reminderValue'] = [reminderValue];
  }

  setTimeout(function(){
    form.reset();
  },0)
  //adding something that is already on the list: sum the quantities into the existing entry rather
  //than ending up with two identical lines
  const duplicate = quantityAware?findDuplicate(list,text,unit,added):undefined;
  if(duplicate){
    const total = clampQuantity((duplicate.quantity||0) + (quantity||0));
    //neither side carries a quantity: the entry is already there, the line adds nothing to it
    const rendered = listsRendered();
    if(total){
      //an undefined unit would not be sent and the added one would stay
      const previous = {quantity:duplicate.quantity||0,unit:duplicate.unit||''};
      ApiServer.taskupdate2({taskId:duplicate.metaId,quantity:total,unit:unit||duplicate.unit});
      //undoing gives the entry its quantity back, the line was never created
      toastItemAdded(itemName(text,quantity,unit),()=>ApiServer.taskupdate2({taskId:duplicate.metaId,...previous}));
    }
    amplify.publish("taskNeedRefresh");
    await rendered;
    revealItems([duplicate.metaId]);
    return;
  }

  //the quick add line carries its own specificities: due date, photo and a single assignee
  function trackQuickAdd(){
    segment.track("Task Created");
    if(formData.assignee?.[0] && formData.assignee[0] !== '$empty'){
      segment.track("Task Assigned",{assignee_count:1});
    }
    if(added.dueDate){
      segment.track("Task Due Date Set");
    }
    if(added.attachment){
      segment.track("Task Photo Added");
    }
  }
  if(!await checkTaskLimitOntry()){
    taskHardLimitReach(taskListId)
    return;
  }
  //an url is added under the title of its page, the url itself goes to the notes
  if(text.indexOf('http')===0){
    const ogBean = await ApiServer.webog(text).catch(()=>undefined);
    formData.description = formData.text;
    formData.text = ogBean?.title||formData.text;
  }
  //not awaited: the create and the refresh below go in the same multiplexed call
  const creating = ApiServer.taskcreate2(formData);
  const rendered = listsRendered();
  amplify.publish("taskNeedRefresh")
  const created = await creating;
  trackQuickAdd();
  toastItemAdded(itemName(created.text,created.quantity,created.unit),()=>ApiServer.metadelete([created.metaId]));
  await rendered;
  //ready for the next item, but the list scrolls to the one added rather than back to the field
  (document.querySelector('.formquickadd .ghost') as HTMLElement)?.focus({preventScroll:true});
  revealItems([created.metaId]);
}

document.addEventListener("selectstart", Html.createDelegateListener(
  '.fw-todo',
  function(ev: Event){
    ev.preventDefault()
  }
));

/* with sorting */
registerDragStart(".fw-todo",async function(this: HTMLElement, ev: DragEvent, dropPromiseBuilder: any){
  const
    dropEvent = await dropPromiseBuilder(this,Array.from(document.querySelectorAll(".fw-todo, .fw-category-header, .fw-tasklist-header"))),
    metaId = Html.getDomMetaId(this),
    target = dropEvent.target as HTMLElement;

  if(target.classList.contains('fw-todo')){
    let
      prevTaskNode = Html.getDomMetaIdNode(target),
      categoryNode = target.closest('.fw-category-header') as HTMLElement;

    const rect = (ev.target as HTMLElement).getBoundingClientRect();
    if(rect.top + rect.height/2 > ev.clientY){
      prevTaskNode = prevTaskNode.previousElementSibling as HTMLElement;
    }
    ApiServer.taskmove({taskId:metaId,prevTaskId:Html.getDomMetaId(prevTaskNode),taskCategoryId:Html.getDomMetaId(categoryNode)})
  }else if(target.classList.contains('fw-category-header')){
    const categoryId = Html.getDomMetaId(target);
    Promise.resolve(ApiServer.taskmove({taskId:metaId,taskCategoryId:categoryId||'$empty'})).then(
      ()=>segment.track("Task Category Changed",{category_name:categoryId?TaskCategoryCache.get(categoryId)?.name:undefined}));

  }else if(target.classList.contains('fw-tasklist-header')){
    Promise.resolve(ApiServer.taskmove({taskId:metaId,taskListId:Html.getDomMetaId(target)||'$empty'})).then(
      ()=>segment.track("Task List Changed"));

  }
  amplify.publish("taskNeedRefresh")

});